import { create } from 'zustand';
import axios from 'axios';

const API = '/api';

// 生成设备ID（基于浏览器指纹）
function getDeviceId() {
  const key = 'steam_device_id';
  let deviceId = localStorage.getItem(key);
  if (!deviceId) {
    // 基于浏览器特征生成唯一ID
    const seed = [
      navigator.userAgent,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.language,
      navigator.hardwareConcurrency || 'unknown'
    ].join('|');
    deviceId = 'DEV_' + btoa(seed).replace(/[+/=]/g, '').substring(0, 32);
    localStorage.setItem(key, deviceId);
  }
  return deviceId;
}

// 获取设备名称
function getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android手机';
  if (/macOS/i.test(ua)) return 'Mac';
  if (/Windows NT 10/i.test(ua)) return 'Windows 10 PC';
  if (/Windows NT 11/i.test(ua)) return 'Windows 11 PC';
  if (/Linux/i.test(ua)) return 'Linux PC';
  return '未知设备';
}

// 关联游客反馈到正式用户
async function linkGuestFeedbacks() {
  const guestToken = localStorage.getItem('guest_feedback_token');
  if (!guestToken) return;
  
  const token = localStorage.getItem('steam_token');
  if (!token) return;
  
  try {
    const res = await axios.post(`${API}/feedbacks/link-guest`, 
      { guest_token: guestToken },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.data.linked > 0) {
      console.log(`已关联 ${res.data.linked} 条游客反馈`);
      // 可以显示提示：您有 X 条游客反馈已关联到账号
    }
  } catch (e) {
    // 忽略关联失败
  }
}

// 过滤AI用户 - 确保永远不会缓存AI用户
function isAiUser(user) {
  if (!user) return false;
  return user.id === 6 || user.email === 'ai@infinite-world.com' || user.is_ai_user || user.isAI;
}

function safeGetUser() {
  const raw = localStorage.getItem('steam_user');
  if (!raw) return null;
  try {
    const user = JSON.parse(raw);
    if (isAiUser(user)) {
      localStorage.removeItem('steam_user');
      localStorage.removeItem('steam_token');
      return null;
    }
    return user;
  } catch (e) {
    return null;
  }
}

export const useAuthStore = create((set, get) => ({
  user: safeGetUser() || null,
  token: localStorage.getItem('steam_token') || null,
  // 本机游客账号凭据：和 steam_token 分开存。
  // 退出登录只清登录状态，绝不清这一条——游客没有邮箱和密码，
  // 删了它就再也回不到自己玩过的账号。
  guestToken: localStorage.getItem('guest_device_token') || null,
  deviceId: getDeviceId(),
  deviceName: getDeviceName(),
  _locationTimer: null,
  isBanned: false,
  banReason: '',
  banUntil: null,

  // 检测当前用户是否被封禁并自动登出
  checkBan: async () => {
    const token = get().token;
    if (!token) return;
    try {
      const res = await axios.get(`${API}/punishments/status/${get().user?.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 3000
      });
      if (res.data.is_banned) {
        set({ isBanned: true, banReason: res.data.ban_reason || '违反社区规则', banUntil: res.data.ban_until });
        // 强制登出
        localStorage.removeItem('steam_token');
        localStorage.removeItem('steam_user');
        setTimeout(() => set({ user: null, token: null, isBanned: false }), 500);
      }
    } catch(e) { /* ignore */ }
  },

  // 更新用户位置（独立函数，登录时和定期调用）
  updateLocation: async () => {
    const token = get().token;
    if (!token) return;
    try {
      /* 原来请求 http://ip-api.com —— HTTPS 页面会被浏览器按混合内容直接拦截。
         ipapi.co 不带 CORS 头，同样会被拦；改用允许跨域的 ipwho.is。 */
      const locRes = await axios.get('https://ipwho.is/', { timeout: 5000 });
      const lat = locRes.data?.latitude;
      const lon = locRes.data?.longitude;
      if (lat && lon) {
        await axios.put(`${API}/profile/me/location`, {
          city: locRes.data.city || '北京',
          lat,
          lon,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (e) { console.warn('[authStore] 定位失败', e.message); }
  },

  // 启动定期定位（每30分钟）
  startLocationTimer: () => {
    if (get()._locationTimer) return;
    const timer = setInterval(() => {
      const t = get().token;
      if (t) get().updateLocation();
      else get().stopLocationTimer();
    }, 30 * 60 * 1000);
    set({ _locationTimer: timer });
  },

  // 停止定期定位
  stopLocationTimer: () => {
    if (get()._locationTimer) {
      clearInterval(get()._locationTimer);
      set({ _locationTimer: null });
    }
  },

  login: async (email, password) => {
    // 登录不再先串行等境外 IP 服务（最坏会拖十几秒）。
    // 线上真实 IP 由 Caddy 透传，后端自己解析地区。
    const deviceId = get().deviceId;
    const deviceName = get().deviceName;
    const res = await axios.post(`${API}/auth/login`, {
      email,
      password,
      deviceId,
      deviceName
    }, { timeout: 15000 });
    if (isAiUser(res.data.user)) {
      localStorage.removeItem('steam_token');
      localStorage.removeItem('steam_user');
      set({ user: null, token: null });
      throw new Error('AI用户已禁用');
    }
    localStorage.setItem('steam_token', res.data.token);
    localStorage.setItem('steam_user', JSON.stringify(res.data.user));
    set({ user: res.data.user, token: res.data.token });
    
    // 位置更新放后台，不阻塞登录按钮
    get().updateLocation();
    get().startLocationTimer();
    
    // 登录成功后关联游客反馈
    linkGuestFeedbacks();
    
    return res.data;
  },

  loginWithPhone: async (phone, code) => {
    const deviceId = get().deviceId;
    const deviceName = get().deviceName;
    const res = await axios.post(`${API}/auth/login`, {
      phone,
      code,
      deviceId,
      deviceName
    }, { timeout: 15000 });
    if (isAiUser(res.data.user)) {
      localStorage.removeItem('steam_token');
      localStorage.removeItem('steam_user');
      set({ user: null, token: null });
      throw new Error('AI用户已禁用');
    }
    localStorage.setItem('steam_token', res.data.token);
    localStorage.setItem('steam_user', JSON.stringify(res.data.user));
    set({ user: res.data.user, token: res.data.token });
    get().updateLocation();
    get().startLocationTimer();
    linkGuestFeedbacks();
    return res.data;
  },

  register: async (username, email, password, agreed, extra = {}) => {
    const deviceId = get().deviceId;
    const deviceName = get().deviceName;
    const res = await axios.post(`${API}/auth/register`, { username, email: email || undefined, password, deviceId, deviceName, agreed: agreed === true, ...extra }, { timeout: 15000 });
    if (isAiUser(res.data.user)) {
      localStorage.removeItem('steam_token');
      localStorage.removeItem('steam_user');
      set({ user: null, token: null });
      throw new Error('AI用户已禁用');
    }

    // 注册即登录：保存 token 和用户信息
    if (res.data.token) {
      localStorage.setItem('steam_token', res.data.token);
      localStorage.setItem('steam_user', JSON.stringify(res.data.user));
      set({ user: res.data.user, token: res.data.token });
    }

    // 注册成功后关联游客反馈
    linkGuestFeedbacks();

    return res.data;
  },

  logout: async () => {
    const { deviceId, token } = get();
    try {
      await axios.post(`${API}/auth/logout`, { deviceId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      // 忽略退出登录时的错误
    }
    localStorage.removeItem('steam_token');
    localStorage.removeItem('steam_user');
    // 注意：这里刻意不删 guest_device_token。
    // 游客账号靠它才能回到原来的账号，删掉等于把账号弄丢。
    get().stopLocationTimer();
    set({ user: null, token: null });
  },

  // ===== 试玩身份 =====
  // 只有用户明确点「先试玩」时，调用方传 { explicit: true } 才允许新建。
  // 已有本机凭据或同设备旧记录仍然直接恢复，不会因为改规则丢进度。
  loginAsGuest: async (options = {}) => {
    const stored = localStorage.getItem('guest_device_token');
    const deviceId = get().deviceId;
    const deviceName = get().deviceName;
    const res = await axios.post(`${API}/auth/guest`, {
      guestToken: stored || undefined,
      deviceId,
      deviceName,
      explicitTrial: options.explicit === true
    }, { timeout: 12000 });
    const { token, user, guestToken } = res.data;
    if (guestToken) localStorage.setItem('guest_device_token', guestToken);
    localStorage.setItem('steam_token', token);
    localStorage.setItem('steam_user', JSON.stringify(user));
    set({ user, token, guestToken: guestToken || stored || null });
    return user;
  },

  // 本机是否存过游客账号（登录页据此显示「用本机账号继续」）
  hasGuestAccount: () => !!localStorage.getItem('guest_device_token'),

  // 检查会话是否被踢出
  checkSession: async () => {
    const { token } = get();
    if (!token) return;
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      if (e.response?.status === 401 && e.response?.data?.code === 'SESSION_CONFLICT') {
        localStorage.removeItem('steam_token');
        localStorage.removeItem('steam_user');
        set({ user: null, token: null });
        // 触发全局通知
        window.dispatchEvent(new CustomEvent('session-kicked', { detail: '其他设备已登录该账号' }));
      }
    }
  },

  updateUser: (userData) => {
    localStorage.setItem('steam_user', JSON.stringify(userData));
    set({ user: userData });
  },

  refreshUser: async () => {
    const { token } = get();
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/me?_t=${Date.now()}`, { 
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store'
      });
      // /auth/me 直接返回 user 对象
      const userData = res.data.user || res.data;
      console.log('[refreshUser] 获取到最新用户数据:', userData);
      localStorage.setItem('steam_user', JSON.stringify(userData));
      set({ user: userData });
      console.log('[refreshUser] store 已更新，新 XP:', userData.xp);
    } catch (e) {
      console.error('刷新用户数据失败', e);
    }
  },

  getHeaders: () => {
    const token = get().token;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  },

  getDeviceId: () => get().deviceId,
  getDeviceName: () => get().deviceName,

  // 通知未读数量
  notifCount: 0,
  fetchNotifCount: async (headers) => {
    try {
      const res = await axios.get(`${API}/notifications/unread`, { headers });
      set({ notifCount: res.data.count || 0 });
    } catch (e) { console.error('fetchNotifCount error', e); }
  },
}));

export const useCartStore = create((set) => ({
  count: 0,
  setCount: (n) => set({ count: n }),
}));

// 应用启动时，如果用户已登录，立即更新一次位置并启动定期更新
// 解决"用户一辈子不重登录"导致位置不更新的问题
(function initLocationOnStartup() {
  const savedToken = localStorage.getItem('steam_token');
  if (!savedToken) return;
  // 等 store 完全初始化后再执行
  setTimeout(() => {
    const store = useAuthStore.getState();
    if (!store.token) return;
    store.updateLocation();
    store.startLocationTimer();
  }, 2000);
})();
